import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { agendaService } from '@/services/agenda'
import type { RendezVous } from '@/services/agenda'
import { CheckCircle, XCircle, Calendar, Clock, Loader2, AlertTriangle } from 'lucide-react'

type PageAction = 'confirm' | 'decline'
type PageStatus = 'loading' | 'loaded' | 'success' | 'already_processed' | 'error'

export default function AppointmentConfirmPage() {
  const { token, action } = useParams<{ token: string; action?: string }>()
  const [status, setStatus] = useState<PageStatus>('loading')
  const [rdv, setRdv] = useState<RendezVous | null>(null)
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState(false)

  // Determine action from URL path
  const pageAction: PageAction = window.location.pathname.includes('/decline') ? 'decline' : 'confirm'

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setError('Lien invalide')
      return
    }

    loadRdv()
  }, [token])

  async function loadRdv() {
    try {
      const data = await agendaService.getRendezVousByToken(token!)
      if (!data) {
        setStatus('error')
        setError('Rendez-vous non trouvé')
        return
      }

      if (data.statut !== 'en_attente') {
        setRdv(data)
        setStatus('already_processed')
        return
      }

      setRdv(data)
      setStatus('loaded')
    } catch {
      setStatus('error')
      setError('Impossible de charger le rendez-vous')
    }
  }

  async function handleAction(act: PageAction) {
    if (!token) return
    setProcessing(true)

    try {
      if (act === 'confirm') {
        const result = await agendaService.confirmRendezVousByToken(token)
        setRdv(result)
      } else {
        const result = await agendaService.declineRendezVousByToken(token)
        setRdv(result)
      }
      setStatus('success')
    } catch (err: any) {
      if (err?.message?.includes('déjà traité')) {
        setStatus('already_processed')
      } else {
        setStatus('error')
        setError('Une erreur est survenue')
      }
    } finally {
      setProcessing(false)
    }
  }

  // Auto-execute action if URL has action path
  useEffect(() => {
    if (status === 'loaded' && pageAction) {
      handleAction(pageAction)
    }
  }, [status])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-50 to-beige-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Loading */}
          {(status === 'loading' || processing) && (
            <div className="p-12 text-center">
              <Loader2 className="w-10 h-10 text-mint-600 animate-spin mx-auto mb-4" />
              <p className="text-brown-600">
                {processing ? 'Traitement en cours...' : 'Chargement...'}
              </p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && rdv && (
            <>
              <div className={`p-6 ${rdv.statut === 'confirme' ? 'bg-mint-500' : 'bg-brown-400'}`}>
                <div className="flex justify-center mb-3">
                  {rdv.statut === 'confirme' ? (
                    <CheckCircle className="w-12 h-12 text-white" />
                  ) : (
                    <XCircle className="w-12 h-12 text-white" />
                  )}
                </div>
                <h1 className="text-xl font-bold text-white text-center">
                  {rdv.statut === 'confirme'
                    ? 'Rendez-vous confirmé !'
                    : 'Rendez-vous décliné'}
                </h1>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-brown-400" />
                    <span className="text-brown-700">{formatDate(rdv.date_heure)}</span>
                  </div>
                  {rdv.description && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-brown-400 mt-0.5" />
                      <span className="text-brown-600">{rdv.description}</span>
                    </div>
                  )}
                </div>
                <p className="mt-6 text-sm text-brown-500 text-center">
                  {rdv.statut === 'confirme'
                    ? 'Merci ! Votre rendez-vous est confirmé.'
                    : 'Ce rendez-vous a été décliné.'}
                </p>
              </div>
            </>
          )}

          {/* Already processed */}
          {status === 'already_processed' && rdv && (
            <>
              <div className="p-6 bg-beige-200">
                <div className="flex justify-center mb-3">
                  <AlertTriangle className="w-12 h-12 text-brown-500" />
                </div>
                <h1 className="text-xl font-bold text-brown-900 text-center">
                  Rendez-vous déjà traité
                </h1>
              </div>
              <div className="p-6">
                <p className="text-brown-600 text-center mb-4">
                  Ce rendez-vous a déjà été{' '}
                  {rdv.statut === 'confirme' ? 'confirmé' : rdv.statut === 'annule' ? 'annulé' : 'traité'}.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-brown-400" />
                    <span className="text-brown-700">{formatDate(rdv.date_heure)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="p-12 text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-brown-900 mb-2">Erreur</h1>
              <p className="text-brown-600">{error}</p>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-brown-400 mt-6">
          LesSablesWeb - CRM
        </p>
      </div>
    </div>
  )
}
